import { HttpResponse, RequestHandler, graphql, http } from 'msw';
import {
  ConfirmSupplierAgreementsMutation,
  GetCustomAgreementsQuery,
  GetCustomAgreementWithContentQuery,
  GetMeQuery,
  GetSupplierAgreementsQuery,
  UpdateCompanyProfileMutation,
  UpdateLocationMutation,
  UpdateUserCurrencyMutation,
  UpdateUserLanguageMutation,
  UpdateUserProfileMutation,
} from '@/generated/gql/graphql';
import { paths } from '@/generated/supplier-experience-api';
import { KpiSpotlightItem } from '@/enterprise/components/InvoiceCentral/KpiSpotlight/useKpiSpotlight';
import { BuyerCashflowData } from '@/enterprise/features/financeiq/useBuyerCashflow';
import { ExchangeRates } from '@/shared/data/useExchangeRates';
import { SEAAccountFeatureConfigsResponse } from '@/shared/data/useFetchAccountFeatureConfigs';

export type OfferStatsStatusResponse =
  paths['/offers/stats/status']['get']['responses']['200']['content']['application/json'];

export type PostBody = paths['/offers/price-discovery']['post']['requestBody']['content']['application/json'];
export type PatchBody = paths['/offers/price-discovery']['patch']['requestBody']['content']['application/json'];
export type BenchmarkPostBody = paths['/offers/benchmark']['post']['requestBody']['content']['application/json'];
export type BenchmarkPatchBody = paths['/offers/benchmark']['patch']['requestBody']['content']['application/json'];
export type StaticPostBody = paths['/offers/static']['post']['requestBody']['content']['application/json'];
export type StaticPatchBody = paths['/offers/static']['patch']['requestBody']['content']['application/json'];
export type ExpressAcceptPostBody =
  paths['/offers/express-accept']['post']['requestBody']['content']['application/json'];
export type PlayableInvoicesRequestBody =
  paths['/playable-invoices']['post']['requestBody']['content']['application/json'];

export type PlayableInvoiceStatsRequestBody =
  paths['/playable-invoices/statistics']['post']['requestBody']['content']['application/json'];

export type ClearingInvoicesRequestBody =
  paths['/clearing-invoices']['post']['requestBody']['content']['application/json'];

export type ClearingInvoiceStatsRequestBody =
  paths['/clearing-invoices/statistics']['post']['requestBody']['content']['application/json'];

const baseApiUrl = import.meta.env.VITE_GATEWAY_URL;

export type SupplierMarketsResponse =
  paths['/supplier-markets']['get']['responses']['200']['content']['application/json'];

export type OffersResponse = paths['/offers']['get']['responses']['200']['content']['application/json'];

export type PlayableInvoicesResponse =
  paths['/playable-invoices']['post']['responses']['200']['content']['application/json'];

export type PlayableInvoiceStatsResponse =
  paths['/playable-invoices/statistics']['post']['responses']['200']['content']['application/json'];

export type ClearingInvoicesResponse =
  paths['/clearing-invoices']['post']['responses']['200']['content']['application/json'];

export type ClearingInvoiceStatsResponse =
  paths['/clearing-invoices/statistics']['post']['responses']['200']['content']['application/json'];

export type PricingSuggestionsResponse =
  paths['/pricing-suggestions']['post']['responses']['200']['content']['application/json'];

export type UserSuppliersResponse = paths['/user/suppliers']['get']['responses']['200']['content']['application/json'];

export type UserTermsResponse = paths['/user/terms']['get']['responses']['200']['content']['application/json'];

export type UserPreferencesResponse =
  paths['/user/preferences']['get']['responses']['200']['content']['application/json'];

export type InvoiceRulesResponse = paths['/invoice-rules']['get']['responses']['200']['content']['application/json'];

export type SupplierMarketClearsResponse =
  paths['/supplier-market-clears']['get']['responses']['200']['content']['application/json'];

export type LockedInvoicesResponse =
  paths['/locked-invoices']['post']['responses']['200']['content']['application/json'];

export type LockedInvoiceStatsResponse =
  paths['/locked-invoices/statistics']['post']['responses']['200']['content']['application/json'];

export type SubstitutedInvoicesRequestBody =
  paths['/substituted-invoices']['post']['requestBody']['content']['application/json'];

export type SubstitutedInvoicesResponse =
  paths['/substituted-invoices']['post']['responses']['200']['content']['application/json'];

export type InstantPayStatsResponse =
  paths['/maker-organizations/{makerOrganizationUuid}/tokenized-invoices-stats']['get']['responses']['200']['content']['application/json'];

export type InstantPayStatsQuery =
  paths['/maker-organizations/{makerOrganizationUuid}/tokenized-invoices-stats']['get']['parameters']['query'];

export type ToggleOfferStatusResponse =
  paths['/offers/{uuid}/toggle-status']['patch']['responses']['200']['content']['application/json'];

export type CreditNotesResponse =
  paths['/buyers/credit-notes']['get']['responses']['200']['content']['application/json'];

export type RecentClearsResponse =
  paths['/supplier-market-clears/recent']['get']['responses']['200']['content']['application/json'];

export type SupplierMarketClearsStatsResponse =
  paths['/supplier-market-clears/stats']['get']['responses']['200']['content']['application/json'];

export type PriceDiscoveryPostResponse =
  paths['/offers/price-discovery']['post']['responses']['201']['content']['application/json'];

export type PriceDiscoveryPatchResponse =
  paths['/offers/price-discovery']['patch']['responses']['202']['content']['application/json'];

export type BuyersExpansionResponse =
  paths['/buyers/expansion']['get']['responses']['200']['content']['application/json'];

export type BuyersLinkingRequestsResponse =
  paths['/buyers/linking-requests']['get']['responses']['200']['content']['application/json'];

export type BuyersResponse = paths['/buyers']['get']['responses']['200']['content']['application/json'];

export type AdjustmentInvoiceStatsResponse =
  paths['/adjustment-invoices/statistics']['post']['responses']['200']['content']['application/json'];

export type ManageExclusionsResponse =
  paths['/playable-invoices/manage-exclusions']['post']['responses']['200']['content']['application/json'];

export type ManageExclusionsBody =
  paths['/playable-invoices/manage-exclusions']['post']['requestBody']['content']['application/json'];
export type GetArtifactsResponse = paths['/artifacts']['get']['responses']['200']['content']['application/json'];

export type SEAMarketingArtifact = GetArtifactsResponse['data']['artifacts'][number];

export type DismissiblesResponse =
  paths['/user/dismissibles']['get']['responses']['200']['content']['application/json'];

export type ClearSchedulesResponse =
  paths['/supplier-markets/clear-schedules']['get']['responses']['200']['content']['application/json'];

export type BuyersExpansionTrendingResponse =
  paths['/buyers/expansion/trending']['get']['responses']['200']['content']['application/json'];

export type BuyersLendingResponse = paths['/buyers/lending']['get']['responses']['200']['content']['application/json'];

export type UserLendingOpportunitiesResponse =
  paths['/user/lending-opportunities']['get']['responses']['200']['content']['application/json'];

export type CreateLendingOpportunityHandoffResponse =
  paths['/user/lending-opportunities/{provider}/handoffs']['post']['responses']['200']['content']['application/json'];

export type CreateLendingOpportunityQuoteResponse =
  paths['/user/lending-opportunities/{provider}/quotes']['post']['responses']['200']['content']['application/json'];

export type AdjustmentInvoicesResponse =
  paths['/adjustment-invoices']['post']['responses']['200']['content']['application/json'];

export type PreferredAutoRenewalResponse =
  paths['/offers/preferred-term/{uuid}/auto-renewal']['patch']['responses']['200']['content']['application/json'];

/** Legacy usercast pay-date-announcements response. Not in the OpenAPI spec so typed inline. */
export interface PayDateAnnouncement {
  isLastDay: boolean;
  payDate: string;
  markets: { makerDivisionName: string; id: number }[];
}

export interface PayDateAnnouncementsResponse {
  data: PayDateAnnouncement[];
}

export interface MswData {
  supplier: NonNullable<GetMeQuery['me']>;
  exchangeRates: ExchangeRates;
  featureConfigs: SEAAccountFeatureConfigsResponse;
  supplierMarkets: SupplierMarketsResponse;
  offers: OffersResponse;
  playableInvoices: PlayableInvoicesResponse;
  playableInvoiceStats: PlayableInvoiceStatsResponse;
  pricingSuggestions: PricingSuggestionsResponse;
  userSuppliers: UserSuppliersResponse;
  userTerms: UserTermsResponse;
  userPreferences: UserPreferencesResponse;
  invoiceRules: InvoiceRulesResponse;
  lockedInvoices: LockedInvoicesResponse;
  lockedInvoiceStats: LockedInvoiceStatsResponse;
  toggleOfferStatus: ToggleOfferStatusResponse;
  supplierMarketClears: SupplierMarketClearsResponse;
  creditNotes: CreditNotesResponse;
  recentClears: RecentClearsResponse;
  supplierMarketClearsStats: SupplierMarketClearsStatsResponse;
  supplierAgreements: GetSupplierAgreementsQuery['agreements']['edges'];
  priceDiscoveryPost: PriceDiscoveryPostResponse;
  priceDiscoveryPatch: PriceDiscoveryPatchResponse;
  buyersExpansion: BuyersExpansionResponse;
  buyersLinkingRequests: BuyersLinkingRequestsResponse;
  buyers: BuyersResponse;
  adjustmentInvoiceStats: AdjustmentInvoiceStatsResponse;
  artifacts: SEAMarketingArtifact[];
  dismissibles: DismissiblesResponse;
  clearSchedules: ClearSchedulesResponse;
  buyersExpansionTrending: BuyersExpansionTrendingResponse;
  buyersLending: BuyersLendingResponse;
  userLendingOpportunities: UserLendingOpportunitiesResponse;
  adjustmentInvoices: AdjustmentInvoicesResponse;
  buyerCashflow: { data: BuyerCashflowData };
  payDateAnnouncements: PayDateAnnouncementsResponse;
  preferredAutoRenewal: PreferredAutoRenewalResponse;
}

export function makeSupplierAgreementsHandler(supplierAgreements: MswData['supplierAgreements']): RequestHandler {
  return graphql.query<GetSupplierAgreementsQuery>('GetSupplierAgreements', () => {
    return HttpResponse.json({ data: { agreements: { edges: supplierAgreements } } });
  });
}

export function makeConfirmSupplierAgreementsHandler(): RequestHandler {
  return graphql.mutation<ConfirmSupplierAgreementsMutation>('ConfirmSupplierAgreements', () => {
    return HttpResponse.json({ data: { createManyAgreementAudits: [] } });
  });
}

export function makeGetMeHandler(supplier: MswData['supplier']): RequestHandler {
  return graphql.query<GetMeQuery>('GetMe', () => {
    return HttpResponse.json({ data: { me: supplier } });
  });
}

export function makeExchangeRatesHandler(rates: MswData['exchangeRates']): RequestHandler {
  return http.get(`${baseApiUrl}/find/api/c2fo/exchange-rate`, () => {
    return HttpResponse.json(rates);
  });
}

export function makeFeatureConfigsHandler(featureConfigs: MswData['featureConfigs']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/accounts/*/feature-configs`, () => {
    return HttpResponse.json(featureConfigs);
  });
}

export function makeSupplierMarketsHandler(
  supplierMarkets: MswData['supplierMarkets'] | (() => MswData['supplierMarkets'])
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/supplier-markets`, () => {
    return HttpResponse.json(typeof supplierMarkets === 'function' ? supplierMarkets() : supplierMarkets);
  });
}

export function makeOffersHandler(offers: MswData['offers'] | (() => MswData['offers'])): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/offers`, () => {
    return HttpResponse.json(typeof offers === 'function' ? offers() : offers);
  });
}

export function makePlayableInvoicesHandler(
  playableInvoices:
    | MswData['playableInvoices']
    | (() => MswData['playableInvoices'])
    | ((body: PlayableInvoicesRequestBody) => MswData['playableInvoices'])
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/playable-invoices`, async ({ request }) => {
    if (typeof playableInvoices === 'function') {
      // Check arity: body-aware callbacks accept the request body; thunks accept nothing.
      if (playableInvoices.length > 0) {
        const body = (await request.json()) as PlayableInvoicesRequestBody;
        return HttpResponse.json(
          (playableInvoices as (b: PlayableInvoicesRequestBody) => MswData['playableInvoices'])(body)
        );
      }

      return HttpResponse.json((playableInvoices as () => MswData['playableInvoices'])());
    }

    return HttpResponse.json(playableInvoices);
  });
}

export function makePlayableInvoiceStatsHandler(
  playableInvoiceStats:
    | MswData['playableInvoiceStats']
    | (() => MswData['playableInvoiceStats'])
    | ((body: PlayableInvoiceStatsRequestBody) => MswData['playableInvoiceStats'])
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/playable-invoices/statistics`, async ({ request }) => {
    if (typeof playableInvoiceStats === 'function') {
      if (playableInvoiceStats.length > 0) {
        const body = (await request.json()) as PlayableInvoiceStatsRequestBody;
        return HttpResponse.json(
          (playableInvoiceStats as (b: PlayableInvoiceStatsRequestBody) => MswData['playableInvoiceStats'])(body)
        );
      }

      return HttpResponse.json((playableInvoiceStats as () => MswData['playableInvoiceStats'])());
    }

    return HttpResponse.json(playableInvoiceStats);
  });
}

export function makePricingSuggestionsHandler(
  pricingSuggestions: MswData['pricingSuggestions'] | (() => MswData['pricingSuggestions'])
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/pricing-suggestions`, () => {
    return HttpResponse.json(typeof pricingSuggestions === 'function' ? pricingSuggestions() : pricingSuggestions);
  });
}

export function makeUserSuppliersHandler(userSuppliers: MswData['userSuppliers']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/user/suppliers`, () => {
    return HttpResponse.json(userSuppliers);
  });
}

export function makeUserTermsHandler(userTerms: MswData['userTerms']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/user/terms`, () => {
    return HttpResponse.json(userTerms);
  });
}

export function makeUserPreferencesHandler(userPreferences: MswData['userPreferences']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/user/preferences`, () => {
    return HttpResponse.json(userPreferences);
  });
}

export function makeUpdateUserPreferencesHandler(userPreferences: MswData['userPreferences']): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/user/preferences`, () => {
    return HttpResponse.json(userPreferences);
  });
}

export function makeInvoiceRulesHandler(invoiceRules: MswData['invoiceRules']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/invoice-rules`, () => {
    return HttpResponse.json(invoiceRules);
  });
}

export function makeLockedInvoicesHandler(
  lockedInvoices: MswData['lockedInvoices'] | (() => MswData['lockedInvoices'])
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/locked-invoices`, () => {
    return HttpResponse.json(typeof lockedInvoices === 'function' ? lockedInvoices() : lockedInvoices);
  });
}

export function makeSubstitutedInvoicesHandler(
  substitutedInvoices:
    | SubstitutedInvoicesResponse
    | ((body: SubstitutedInvoicesRequestBody) => SubstitutedInvoicesResponse | Response)
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/substituted-invoices`, async ({ request }) => {
    if (typeof substitutedInvoices !== 'function') {
      return HttpResponse.json(substitutedInvoices);
    }

    const result = substitutedInvoices((await request.json()) as SubstitutedInvoicesRequestBody);
    return result instanceof Response ? result : HttpResponse.json(result);
  });
}

/**
 * Instant Pay substitution stats for one customer (Substitution Records page). The resolver gets the
 * path's maker organization UUID and the query, so specs can return per-customer rows. It may be async,
 * so specs can hold a response open.
 */
export function makeInstantPayStatsHandler(
  stats:
    | InstantPayStatsResponse
    | ((args: {
        makerOrganizationUuid: string;
        query: InstantPayStatsQuery;
      }) => InstantPayStatsResponse | Response | Promise<InstantPayStatsResponse | Response>)
): RequestHandler {
  return http.get(
    `${baseApiUrl}/api/supplier-experience/maker-organizations/:makerOrganizationUuid/tokenized-invoices-stats`,
    async ({ params, request }) => {
      if (typeof stats !== 'function') {
        return HttpResponse.json(stats);
      }

      const searchParams = new URL(request.url).searchParams;
      const result = await stats({
        makerOrganizationUuid: String(params.makerOrganizationUuid),
        query: {
          type: 'substitution',
          startDate: searchParams.get('startDate') ?? undefined,
          endDate: searchParams.get('endDate') ?? undefined,
        },
      });
      return result instanceof Response ? result : HttpResponse.json(result);
    }
  );
}

/** Instant Pay substitution statement CSV. Returns an empty CSV named after the statement and type. */
export function makeInstantPayStatementCsvHandler(): RequestHandler {
  return http.get(
    `${baseApiUrl}/api/supplier-experience/maker-organizations/:makerOrganizationUuid/tokenized-invoices-statements/:statementId`,
    ({ params, request }) => {
      const type = new URL(request.url).searchParams.get('type') ?? 'substitution';

      return new HttpResponse('', {
        status: 200,
        headers: {
          'Content-Type': 'text/csv',
          'Content-Disposition': `attachment; filename="${String(params.statementId)}_${type}.csv"`,
        },
      });
    }
  );
}

export function makeLockedInvoiceStatsHandler(
  lockedInvoiceStats: MswData['lockedInvoiceStats'] | (() => MswData['lockedInvoiceStats'])
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/locked-invoices/statistics`, () => {
    return HttpResponse.json(typeof lockedInvoiceStats === 'function' ? lockedInvoiceStats() : lockedInvoiceStats);
  });
}

export function makeClearingInvoicesHandler(
  clearingInvoices: ClearingInvoicesResponse | ((body: ClearingInvoicesRequestBody) => ClearingInvoicesResponse)
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/clearing-invoices`, async ({ request }) => {
    const response =
      typeof clearingInvoices === 'function'
        ? clearingInvoices((await request.json()) as ClearingInvoicesRequestBody)
        : clearingInvoices;
    return HttpResponse.json(response);
  });
}

export function makeClearingInvoiceStatsHandler(
  clearingInvoiceStats:
    | ClearingInvoiceStatsResponse
    | ((body: ClearingInvoiceStatsRequestBody) => ClearingInvoiceStatsResponse)
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/clearing-invoices/statistics`, async ({ request }) => {
    const response =
      typeof clearingInvoiceStats === 'function'
        ? clearingInvoiceStats((await request.json()) as ClearingInvoiceStatsRequestBody)
        : clearingInvoiceStats;
    return HttpResponse.json(response);
  });
}

export function makeToggleOfferStatusHandler(
  toggleOfferStatus: MswData['toggleOfferStatus'],
  onToggle?: (uuid: string) => Promise<void>
): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/:uuid/toggle-status`, async ({ params }) => {
    if (onToggle) {
      await onToggle(String(params.uuid));
    }

    return HttpResponse.json(toggleOfferStatus);
  });
}

export function makeSupplierMarketClearsHandler(
  supplierMarketClears: MswData['supplierMarketClears'] | ((request: Request) => MswData['supplierMarketClears'])
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/supplier-market-clears`, ({ request }) => {
    return HttpResponse.json(
      typeof supplierMarketClears === 'function' ? supplierMarketClears(request) : supplierMarketClears
    );
  });
}

export function makeCreditNotesHandler(creditNotes: MswData['creditNotes']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/buyers/credit-notes`, () => {
    return HttpResponse.json(creditNotes);
  });
}

export function makeRecentClearsHandler(
  recentClears:
    | MswData['recentClears']
    | (() => MswData['recentClears'])
    | ((request: Request) => MswData['recentClears'])
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/supplier-market-clears/recent`, ({ request }) => {
    return HttpResponse.json(
      typeof recentClears === 'function'
        ? recentClears.length > 0
          ? (recentClears as (r: Request) => MswData['recentClears'])(request)
          : (recentClears as () => MswData['recentClears'])()
        : recentClears
    );
  });
}

export function makeSupplierMarketClearsStatsHandler(
  supplierMarketClearsStats:
    | MswData['supplierMarketClearsStats']
    | ((request: Request) => MswData['supplierMarketClearsStats'])
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/supplier-market-clears/stats`, ({ request }) => {
    return HttpResponse.json(
      typeof supplierMarketClearsStats === 'function' ? supplierMarketClearsStats(request) : supplierMarketClearsStats
    );
  });
}

export function makePriceDiscoveryPostHandler(
  priceDiscoveryPost: MswData['priceDiscoveryPost'],
  onSubmit?: (body: PostBody) => Promise<string>
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/offers/price-discovery`, async ({ request }) => {
    if (onSubmit) {
      const body = (await request.json()) as PostBody;
      const uuid = await onSubmit(body);
      return HttpResponse.json({ data: [{ uuid }] }, { status: 201 });
    }

    return HttpResponse.json(priceDiscoveryPost, { status: 201 });
  });
}

export function makePriceDiscoveryPatchHandler(
  priceDiscoveryPatch?: MswData['priceDiscoveryPatch'],
  onEdit?: (body: PatchBody) => Promise<void>
): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/price-discovery`, async ({ request }) => {
    if (onEdit && priceDiscoveryPatch) {
      const body = (await request.json()) as PatchBody;
      await onEdit(body);
      return HttpResponse.json(priceDiscoveryPatch, { status: 202 });
    }

    return new HttpResponse(null, { status: 204 });
  });
}

export function makeManageExclusionsHandler(
  onManage?: (body: ManageExclusionsBody) => Promise<ManageExclusionsResponse['data']>
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/playable-invoices/manage-exclusions`, async ({ request }) => {
    if (onManage) {
      const body = (await request.json()) as ManageExclusionsBody;
      const data = await onManage(body);
      return HttpResponse.json({ data } satisfies ManageExclusionsResponse);
    }

    // Default: return an empty stats response (no-op for test suite).
    return new HttpResponse(null, { status: 204 });
  });
}

export function makeOfferStatsStatusHandler(): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/offers/stats/status`, () => {
    return HttpResponse.json({ data: [] } satisfies OfferStatsStatusResponse);
  });
}

export function makeBenchmarkPostHandler(onSubmit?: (body: BenchmarkPostBody) => Promise<string>): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/offers/benchmark`, async ({ request }) => {
    if (onSubmit) {
      const body = (await request.json()) as BenchmarkPostBody;
      const uuid = await onSubmit(body);
      return HttpResponse.json({ data: [{ uuid }] }, { status: 201 });
    }

    return HttpResponse.json({ data: [] }, { status: 201 });
  });
}

export function makeBenchmarkPatchHandler(onEdit?: (body: BenchmarkPatchBody) => Promise<void>): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/benchmark`, async ({ request }) => {
    if (onEdit) {
      const body = (await request.json()) as BenchmarkPatchBody;
      await onEdit(body);
    }

    return new HttpResponse(null, { status: 204 });
  });
}

export function makeStaticPostHandler(onSubmit?: (body: StaticPostBody) => Promise<string>): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/offers/static`, async ({ request }) => {
    if (onSubmit) {
      const body = (await request.json()) as StaticPostBody;
      const uuid = await onSubmit(body);
      return HttpResponse.json({ data: [{ uuid }] }, { status: 201 });
    }

    return HttpResponse.json({ data: [] }, { status: 201 });
  });
}

export function makeStaticPatchHandler(onEdit?: (body: StaticPatchBody) => Promise<void>): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/static`, async ({ request }) => {
    if (onEdit) {
      const body = (await request.json()) as StaticPatchBody;
      await onEdit(body);
    }

    return new HttpResponse(null, { status: 204 });
  });
}

export function makeBuyersExpansionHandler(buyersExpansion: MswData['buyersExpansion']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/buyers/expansion`, () => {
    return HttpResponse.json(buyersExpansion);
  });
}

export function makeBuyersLinkingRequestsHandler(
  buyersLinkingRequests: MswData['buyersLinkingRequests']
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/buyers/linking-requests`, () => {
    return HttpResponse.json(buyersLinkingRequests);
  });
}

export function makeBuyersHandler(buyers: MswData['buyers']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/buyers`, () => {
    return HttpResponse.json(buyers);
  });
}

export function makeAdjustmentInvoiceStatsHandler(
  adjustmentInvoiceStats: MswData['adjustmentInvoiceStats']
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/adjustment-invoices/statistics`, () => {
    return HttpResponse.json(adjustmentInvoiceStats);
  });
}

/**
 * Silences the SSE connection in demo/test mode by returning a well-formed SSE response with a
 * very high retry directive (~24 h). This parks the eventsource-client so it stops reconnecting
 * and MSW stops logging unmatched-request warnings. No events are emitted — demo mode does not
 * need real-time market stats pushes.
 */
export function makePushEventsHandler(): RequestHandler {
  return http.get(`${baseApiUrl}/api/c2fo/push-events`, () => {
    const stream = new ReadableStream({
      start(controller) {
        controller.enqueue(new TextEncoder().encode('retry:86400000\n\n'));
        // Keep the stream open; the client won't reconnect for ~24 h.
      },
    });
    return new Response(stream, {
      headers: {
        'Content-Type': 'text/event-stream',
        'Cache-Control': 'no-cache',
      },
    });
  });
}

export function makeArtifactsHandler(artifacts: MswData['artifacts']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/artifacts`, ({ request }) => {
    // Artifacts are fetched per-type (NAVIGATION vs MARKETING_MESSAGE) in separate calls.
    // Honor the requested artifactTypes so each call only returns its slice.
    const requestedTypes = new URLSearchParams(new URL(request.url).search).getAll('artifactTypes[]');
    const filteredArtifacts =
      requestedTypes.length > 0
        ? artifacts.filter((artifact) => requestedTypes.includes(artifact.artifactType))
        : artifacts;

    const byPlacement = filteredArtifacts.reduce<Record<string, string[]>>((acc, artifact) => {
      for (const placement of artifact.placements ?? []) {
        acc[placement] = [...(acc[placement] ?? []), artifact.uuid];
      }

      return acc;
    }, {});

    return HttpResponse.json({
      data: { artifacts: filteredArtifacts, byPlacement },
    } satisfies GetArtifactsResponse);
  });
}

// ── Account settings: GraphQL mutations ──────────────────────────────────────

export function makeUpdateUserProfileHandler(): RequestHandler {
  return graphql.mutation<UpdateUserProfileMutation>('UpdateUserProfile', () => {
    return HttpResponse.json({ data: { users: { success: true } } });
  });
}

export function makeUpdateCompanyProfileHandler(): RequestHandler {
  return graphql.mutation<UpdateCompanyProfileMutation>('UpdateCompanyProfile', () => {
    return HttpResponse.json({ data: { users: { success: true } } });
  });
}

export function makeUpdateLocationHandler(): RequestHandler {
  return graphql.mutation<UpdateLocationMutation>('UpdateLocation', () => {
    return HttpResponse.json({ data: { users: { success: true } } });
  });
}

export function makeUpdateUserCurrencyHandler(): RequestHandler {
  return graphql.mutation<UpdateUserCurrencyMutation>('UpdateUserCurrency', () => {
    return HttpResponse.json({ data: { users: { success: true } } });
  });
}

export function makeUpdateUserLanguageHandler(): RequestHandler {
  return graphql.mutation<UpdateUserLanguageMutation>('UpdateUserLanguage', () => {
    return HttpResponse.json({ data: { users: { success: true } } });
  });
}

export function makeGetCustomAgreementsHandler(): RequestHandler {
  return graphql.query<GetCustomAgreementsQuery>('GetCustomAgreements', () => {
    return HttpResponse.json({ data: { customAgreements: [] } });
  });
}

export function makeGetCustomAgreementWithContentHandler(): RequestHandler {
  return graphql.query<GetCustomAgreementWithContentQuery>('GetCustomAgreementWithContent', () => {
    return HttpResponse.json({
      data: {
        customAgreementWithContent: {
          content: '',
          agreementTitle: '',
          updatedAt: null,
          updatedBy: null,
        },
      },
    });
  });
}

// ── Account settings: REST ────────────────────────────────────────────────────

/** Forget-me request — returns 204 so the flow completes without error in demo. */
export function makeForgetMeHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/c2fo/user/forget`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

/** Forgot-password request — returns 204 so the flow completes without error in demo. */
export function makeForgotPasswordHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/auth-ui/forgot-password`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Dismissibles ──────────────────────────────────────────────────────────────

export function makeDismissiblesHandler(dismissibles: MswData['dismissibles']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/user/dismissibles`, () => {
    return HttpResponse.json(dismissibles);
  });
}

export function makeDismissHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/user/dismissibles`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Clear schedules ────────────────────────────────────────────────────────────

export function makeClearSchedulesHandler(clearSchedules: MswData['clearSchedules']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/supplier-markets/clear-schedules`, () => {
    return HttpResponse.json(clearSchedules);
  });
}

// ── Buyers expansion trending ──────────────────────────────────────────────────

export function makeBuyersExpansionTrendingHandler(
  buyersExpansionTrending: MswData['buyersExpansionTrending']
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/buyers/expansion/trending`, () => {
    return HttpResponse.json(buyersExpansionTrending);
  });
}

/** Vote on a trending expansion request — returns 204 (success) in demo. */
export function makeBuyersExpansionTrendingVoteHandler(): RequestHandler {
  return http.put(`${baseApiUrl}/api/supplier-experience/buyers/expansion/trending/*/vote`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Buyers lending ─────────────────────────────────────────────────────────────

export function makeBuyersLendingHandler(buyersLending: MswData['buyersLending']): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/buyers/lending`, () => {
    return HttpResponse.json(buyersLending);
  });
}

/** Create lending request — returns 204 so the form submits without error in demo. */
export function makeCreateBuyersLendingHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/buyers/lending`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Lending opportunities ──────────────────────────────────────────────────────

export function makeUserLendingOpportunitiesHandler(
  userLendingOpportunities: MswData['userLendingOpportunities']
): RequestHandler {
  return http.get(`${baseApiUrl}/api/supplier-experience/user/lending-opportunities`, () => {
    return HttpResponse.json(userLendingOpportunities);
  });
}

export function makeCreateMonduHandoffHandler(
  response: CreateLendingOpportunityHandoffResponse = {
    data: {
      handoffUuid: '00000000-0000-4000-8000-000000000001',
      provider: 'MONDU',
      providerApplicationId: 'mondu-supplier-uuid',
      nextAction: {
        kind: 'redirect',
        redirectUrl: 'https://c2fo-invoice.demo.mondu.ai/?email=supplier%40example.com',
      },
    },
  }
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/user/lending-opportunities/MONDU/handoffs`, () => {
    return HttpResponse.json(response);
  });
}

const QUOTES_URL = `${baseApiUrl}/api/supplier-experience/user/lending-opportunities/MONDU/quotes`;

const defaultCreateMonduQuoteResponse: CreateLendingOpportunityQuoteResponse = {
  data: {
    quoteId: '00000000-0000-4000-8000-000000000002',
    accountId: 'account-1',
    provider: 'MONDU',
    status: 'quoted',
    supplierAprRange: { min: 10.5, max: 18.25 },
    customers: [],
    supplierProfile: {
      companyName: null,
      registeredAddress: {
        addressLine1: null,
        addressLine2: null,
        street: null,
        city: null,
        zipCode: null,
        countryCode: null,
      },
      registrationNumber: null,
      revenue: null,
    },
  },
};

const failedCreateMonduQuoteBody = { errors: [{ code: 500, title: 'Provider Quote Failed' }] };

export function makeCreateMonduQuoteHandler(
  response: CreateLendingOpportunityQuoteResponse = defaultCreateMonduQuoteResponse
): RequestHandler {
  return http.post(QUOTES_URL, () => {
    return HttpResponse.json(response);
  });
}

/** A quotes POST that stays pending until `succeed` or `fail`, so a test can see the loading modal. */
export function makeHeldCreateMonduQuoteHandler(): {
  handler: RequestHandler;
  succeed: (response?: CreateLendingOpportunityQuoteResponse) => void;
  fail: () => void;
} {
  const { promise: pending, resolve: finish } = Promise.withResolvers<ReturnType<typeof HttpResponse.json>>();

  return {
    handler: http.post(QUOTES_URL, () => pending),
    succeed: (response = defaultCreateMonduQuoteResponse) => {
      finish(HttpResponse.json(response));
    },
    fail: () => {
      finish(HttpResponse.json(failedCreateMonduQuoteBody, { status: 500 }));
    },
  };
}

export function makeFailedCreateMonduQuoteHandler(): RequestHandler {
  return http.post(QUOTES_URL, () => HttpResponse.json(failedCreateMonduQuoteBody, { status: 500 }));
}

// ── Adjustment invoices (list) ─────────────────────────────────────────────────

export function makeAdjustmentInvoicesHandler(adjustmentInvoices: MswData['adjustmentInvoices']): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/adjustment-invoices`, () => {
    return HttpResponse.json(adjustmentInvoices);
  });
}

// ── Information requests ───────────────────────────────────────────────────────

/** Information request for preferred-term offers — returns 204 in demo. */
export function makeInformationRequestPreferredTermHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/information-request/preferred-term`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

/** Information request for invoice rules — returns 204 in demo. */
export function makeInformationRequestInvoiceRulesHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/information-request/invoice-rules`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Playable invoices CSV ──────────────────────────────────────────────────────

/** Returns a CSV response for the playable-invoice download flow. */
export function makePlayableInvoicesCsvHandler(
  buildCsv: (body: PlayableInvoicesRequestBody) => string = () => ''
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/playable-invoices/csv`, async ({ request }) => {
    const body = (await request.json()) as PlayableInvoicesRequestBody;
    return new HttpResponse(buildCsv(body), { status: 200, headers: { 'Content-Type': 'text/csv' } });
  });
}

/** Locked (ineligible) invoices CSV — empty body so the download UI doesn't error in demo. */
export function makeLockedInvoicesCsvHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/locked-invoices/csv`, () => {
    return new HttpResponse('', { status: 200, headers: { 'Content-Type': 'text/csv' } });
  });
}

/** Adjustment invoices CSV — empty body so the download UI doesn't error in demo. */
export function makeAdjustmentInvoicesCsvHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/adjustment-invoices/csv`, () => {
    return new HttpResponse('', { status: 200, headers: { 'Content-Type': 'text/csv' } });
  });
}

/** Single supplier market clear (TMC pending clear) CSV — empty body so the download UI doesn't error in demo. */
export function makeSupplierMarketClearCsvHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/supplier-market-clears/:clearId/csv`, ({ params }) => {
    return new HttpResponse('', {
      status: 200,
      headers: {
        'Content-Type': 'text/csv',
        'Content-Disposition': `attachment; filename="${String(params.clearId)}_supplier-market-clear.csv"`,
      },
    });
  });
}

/** Legacy eligible-invoice CSV (user-source path) — returns 201 so the download flow completes. */
export function makeEligibleInvoicesCsvHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/c2fo/user-source/taker/invoices/eligible/csv`, () => {
    return new HttpResponse(null, { status: 201 });
  });
}

/**
 * Legacy division invoice routes return raw arrays and `{ count, subTotal }` stats.
 * Keep these explicit so demo rollback paths never receive the catch-all `{ data: [] }` shape.
 */
export function makeLegacyInvoiceHandlers(): RequestHandler[] {
  const basePath = `${baseApiUrl}/api/c2fo/taker/:takerId/market/:marketId/invoices`;
  const invoiceTypes = ['ineligible', 'adjustments', 'awarded'] as const;

  return invoiceTypes.flatMap((invoiceType) => [
    http.get(`${basePath}/${invoiceType}/stats`, () => HttpResponse.json({ count: 0, subTotal: 0 })),
    http.get(`${basePath}/${invoiceType}`, () => HttpResponse.json([])),
  ]);
}

// ── FinanceIQ: buyer cashflow ──────────────────────────────────────────────────

export function makeBuyerCashflowHandler(buyerCashflow: MswData['buyerCashflow']): RequestHandler {
  return http.get(`${baseApiUrl}/api/invoice-hub/v1/buyer-cashflow`, () => {
    return HttpResponse.json(buyerCashflow);
  });
}

// ── FinanceIQ: KPI spotlight ───────────────────────────────────────────────────

export function makeKpiSpotlightHandler(spotlight: KpiSpotlightItem | null): RequestHandler {
  return http.get(`${baseApiUrl}/api/invoice-hub/v1/kpi-spotlight`, ({ request }) => {
    const { searchParams } = new URL(request.url);
    return HttpResponse.json({
      data: {
        makerOrganizationUuid: searchParams.get('makerOrganizationUuid') ?? '',
        currency: searchParams.get('currency') ?? '',
        spotlight,
      },
    });
  });
}

// ── Pay-date announcements (legacy usercast) ───────────────────────────────────

export function makePayDateAnnouncementsHandler(payDateAnnouncements: MswData['payDateAnnouncements']): RequestHandler {
  return http.get(`${baseApiUrl}/usercast/api/c2fo/market/pay-date-announcements`, () => {
    return HttpResponse.json(payDateAnnouncements);
  });
}

// ── Preferred-term auto-renewal ────────────────────────────────────────────────

export function makePreferredAutoRenewalHandler(data: MswData['preferredAutoRenewal']): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/preferred-term/*/auto-renewal`, () => {
    return HttpResponse.json(data);
  });
}

/** Request renewal for a preferred-term offer — returns 204. */
export function makePreferredTermRequestRenewalHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/offers/preferred-term/*/request-renewal`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Delete offer ───────────────────────────────────────────────────────────────

/** Delete any offer by UUID — returns 204. */
export function makeDeleteOfferHandler(): RequestHandler {
  return http.delete(`${baseApiUrl}/api/supplier-experience/offers/*`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Toggle-exclude-new-invoices ────────────────────────────────────────────────

/** Toggle exclude-new-invoices for price-discovery offers — returns 204. */
export function makePriceDiscoveryToggleExcludeNewInvoicesHandler(): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/price-discovery/toggle-exclude-new-invoices`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

/** Toggle exclude-new-invoices for benchmark offers — returns 204. */
export function makeBenchmarkToggleExcludeNewInvoicesHandler(): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/benchmark/toggle-exclude-new-invoices`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

/** Toggle exclude-new-invoices for static offers — returns 204. */
export function makeStaticToggleExcludeNewInvoicesHandler(): RequestHandler {
  return http.patch(`${baseApiUrl}/api/supplier-experience/offers/static/toggle-exclude-new-invoices`, () => {
    return new HttpResponse(null, { status: 204 });
  });
}

// ── Buyer linking requests (create) ───────────────────────────────────────────

/** Create buyer linking request — returns `{ data: [] }`. */
export function makeCreateBuyerLinkingRequestHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/buyers/linking-requests`, () => {
    return HttpResponse.json({ data: [] });
  });
}

// ── Buyer expansion request (create) ──────────────────────────────────────────

/** Create buyer expansion request — returns `{ data: [] }`. */
export function makeCreateBuyerExpansionRequestHandler(): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/buyers/expansion`, () => {
    return HttpResponse.json({ data: [] });
  });
}

export function makeExpressAcceptPostHandler(
  onSubmit?: (body: ExpressAcceptPostBody) => Promise<string>
): RequestHandler {
  return http.post(`${baseApiUrl}/api/supplier-experience/offers/express-accept`, async ({ request }) => {
    if (onSubmit) {
      const body = (await request.json()) as ExpressAcceptPostBody;
      const uuid = await onSubmit(body);
      return HttpResponse.json({ data: [{ uuid }] }, { status: 201 });
    }

    return HttpResponse.json({ data: [{ uuid: `DEMO-EA-${crypto.randomUUID()}` }] }, { status: 201 });
  });
}
